import React from "react";
import { useQuery, gql } from "@apollo/client";
import Loader from "../../Loader";

const GET_BOOKING_FUNNEL = gql`
  query getBookingFunnel($startDate: String!, $endDate: String!, $employeeId: ID, $employeeIds: [ID]) {
    getBookingFunnel(startDate: $startDate, endDate: $endDate, employeeId: $employeeId, employeeIds: $employeeIds) {
      status
      message
      data {
        totalCompleted
        confirmed
        headedLocation
        reachedLocation
        serviceStarted
        serviceFinished
        serviceScanned {
          total
          forceScanTrue
          forceScanFalse
        }
        completionExceptions {
          total
          timerStartSkipped
          paidOutsideRazorpay
          overpaidOnline
        }
        avgMinsToHead
        avgMinsToReach
        avgMinsToStart
        avgMinsToScan
        avgMinsToFinish
        avgMinsToScanForceTrue
        avgMinsToScanForceFalse
      }
    }
  }
`;

const BookingFunnelWidget = ({ filters, onDropClick }) => {
  const { data, loading, error } = useQuery(GET_BOOKING_FUNNEL, {
    variables: { 
      startDate: filters.startDate, 
      endDate: filters.endDate,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds?.length > 0 ? filters.employeeIds : undefined,
    },
    fetchPolicy: "network-only",
  });

  if (loading) return <div className="p-6"><Loader /></div>;
  if (error) return <div className="text-red-500 p-6">Error loading funnel</div>;

  const funnel = data?.getBookingFunnel?.data;
  if (!funnel) return null;

  const total = funnel.totalCompleted || 1; // avoid division by zero

  const steps = [
    { label: "Completed Bookings (Base)", count: funnel.totalCompleted, dropStage: null },
    { label: "Confirmed", count: funnel.confirmed, dropStage: "NOT_CONFIRMED" },
    { label: "Headed to Location", count: funnel.headedLocation, dropStage: "NOT_HEADED", avgTime: funnel.avgMinsToHead },
    { label: "Reached Location", count: funnel.reachedLocation, dropStage: "NOT_REACHED", avgTime: funnel.avgMinsToReach },
    { label: "Service Started", count: funnel.serviceStarted, dropStage: "NOT_STARTED", avgTime: funnel.avgMinsToStart },
    { label: "Inventory Scanned", count: funnel.serviceScanned?.total, dropStage: "NOT_SCANNED", avgTime: funnel.avgMinsToScan },
    { label: "Service Finished", count: funnel.serviceFinished, dropStage: "NOT_FINISHED", avgTime: funnel.avgMinsToFinish },
  ];

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800">
      <h3 className="text-[#FBB033] font-bold text-xl mb-6">Order Lifecycle Funnel</h3>
      <div className="flex flex-col">
        {steps.map((step, index) => {
          const percentage = Math.round((step.count / total) * 100) || 0;
          const dropCount = index > 0 ? steps[index - 1].count - step.count : 0;
          const dropPercentage = index > 0 ? Math.round((dropCount / steps[index - 1].count) * 100) || 0 : 0;

          return (
            <React.Fragment key={index}>
              {/* Drop connecting logic */}
              {index > 0 && (
                <div className="flex justify-end pr-4 py-2">
                  {dropCount > 0 ? (
                    <div className="flex items-center space-x-3">
                      {step.avgTime != null && (
                        <div className="text-[10px] text-gray-400 font-mono bg-gray-800 px-2 py-1 rounded-md">
                          ⏱️ Avg: {step.avgTime} mins
                        </div>
                      )}
                      <button
                        onClick={() => onDropClick && onDropClick(step.dropStage)}
                        className="text-xs bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-1 rounded-md hover:bg-red-500/20 transition-colors flex items-center space-x-1"
                      >
                        <span>▼ Drop: {dropCount}</span>
                        <span className="text-[10px] opacity-75">({dropPercentage}%)</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      {step.avgTime != null && (
                        <div className="text-[10px] text-gray-400 font-mono bg-gray-800 px-2 py-1 rounded-md">
                          ⏱️ Avg: {step.avgTime} mins
                        </div>
                      )}
                      <div className="text-xs text-gray-600 px-2 py-1">
                        No Drop
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step Bar */}
              <div className="flex flex-col">
                <div className="flex justify-between text-gray-300 text-sm mb-1 font-medium">
                  <span>{step.label}</span>
                  <span>{step.count} ({percentage}%)</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-4 relative overflow-hidden">
                  <div
                    className="bg-[#FBB033] h-full transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>

                {/* Custom Subtext for Scanned */}
                {step.label === "Inventory Scanned" && (
                  <div className="text-xs text-gray-400 mt-2 pl-1 flex items-center space-x-4 italic">
                    <span>
                      Force Skipped:{" "}
                      <button
                        onClick={() => onDropClick && onDropClick("SCANNED_FORCE_TRUE")}
                        className="text-red-400 font-bold hover:underline"
                      >
                        {funnel.serviceScanned?.forceScanTrue || 0}
                      </button>
                      {funnel.avgMinsToScanForceTrue != null && (
                        <span className="text-[10px] text-gray-500 ml-1">
                          (Avg: {funnel.avgMinsToScanForceTrue} mins)
                        </span>
                      )}
                    </span>
                    <span>
                      Valid Scans:{" "}
                      <button
                        onClick={() => onDropClick && onDropClick("SCANNED_FORCE_FALSE")}
                        className="text-green-400 font-bold hover:underline"
                      >
                        {funnel.serviceScanned?.forceScanFalse || 0}
                      </button>
                      {funnel.avgMinsToScanForceFalse != null && (
                        <span className="text-[10px] text-gray-500 ml-1">
                          (Avg: {funnel.avgMinsToScanForceFalse} mins)
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {/* Finished by the professional past a check that used to
                    block End Service; the drop above is bookings an admin
                    closed by verifying them without End Service. */}
                {step.dropStage === "NOT_FINISHED" && (
                  <div className="text-xs text-gray-400 mt-2 pl-1 flex flex-wrap items-center gap-x-4 gap-y-1 italic">
                    <span>
                      Finished with exceptions:{" "}
                      <button
                        onClick={() => onDropClick && onDropClick("COMPLETION_EXCEPTION")}
                        className="text-amber-400 font-bold hover:underline"
                      >
                        {funnel.completionExceptions?.total || 0}
                      </button>
                    </span>
                    <span>Timer start not confirmed: {funnel.completionExceptions?.timerStartSkipped || 0}</span>
                    <span>Paid outside Razorpay: {funnel.completionExceptions?.paidOutsideRazorpay || 0}</span>
                    <span>Overpaid online: {funnel.completionExceptions?.overpaidOnline || 0}</span>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default BookingFunnelWidget;
